import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Phone, MessageSquare, Star } from "lucide-react";
import { api, fmt, imgUrl } from "@/lib/api";

const HERO_POSTER = imgUrl("/api/files/bookedbybenj/site/hero.jpeg");

const SERVICES = [
  { to: "/models", title: "Models", desc: "Agency-vetted talent for music videos, film, events and shoots.", img: imgUrl("/api/files/bookedbybenj/site/model.png") },
  { to: "/cars", title: "Cars", desc: "Exotic and luxury fleet for productions — prop usage, fully managed.", img: imgUrl("/api/files/bookedbybenj/site/car.png") },
  { to: "/houses", title: "Houses", desc: "Mansions and estates across LA for shoots, events and content days.", img: imgUrl("/api/files/bookedbybenj/site/mansion.png") },
  { to: "/marketing", title: "Marketing", desc: "YouTube campaigns, press runs, IG promo and viral rollouts.", img: imgUrl("/api/files/bookedbybenj/site/artist.png") },
];

const LOGOS = [
  imgUrl("/api/files/bookedbybenj/site/logo2-300.png"),
  imgUrl("/api/files/bookedbybenj/site/logo2-atlantic.png"),
  imgUrl("/api/files/bookedbybenj/site/logo2-defjam.png"),
  imgUrl("/api/files/bookedbybenj/site/logo2-rocnation.gif"),
  imgUrl("/api/files/bookedbybenj/site/logo2-qc.png"),
];

const DEFAULT_GALLERY = [
  "https://images.unsplash.com/photo-1568782517100-09bf22d88c2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwbW9kZWwlMjBkYXJrfGVufDB8fHx8MTc4ODA0NDQ5N3ww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1505843513577-22bb7d21e455?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtYW5zaW9uJTIwZGFya3xlbnwwfHx8fDE3ODgwNDQ0OTd8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1632582204758-5ac65783517a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4ODA0NDQ5N3ww&ixlib=rb-4.1.0&q=85",
  imgUrl("/api/files/bookedbybenj/site/model.png"),
  imgUrl("/api/files/bookedbybenj/site/car.png"),
  "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4ODA0NDQ5N3ww&ixlib=rb-4.1.0&q=85",
];

const DEFAULT_MV = [
  "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4ODA0NDQ5N3ww&ixlib=rb-4.1.0&q=85",
  imgUrl("/api/files/bookedbybenj/site/artist.png"),
  "https://images.unsplash.com/photo-1632582204758-5ac65783517a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvfGVufDB8fHx8MTc4ODA0NDQ5N3ww&ixlib=rb-4.1.0&q=85",
];

export default function Home() {
  const [content, setContent] = useState({});
  const [ytServices, setYtServices] = useState([]);

  useEffect(() => {
    api.get("/site/content").then((r) => setContent(r.data || {})).catch(() => {});
    api.get("/marketing-services").then((r) => {
      const yt = (r.data || []).filter((s) => s.platform === "youtube" && /silver|gold|platinum/i.test(s.name));
      yt.sort((a, b) => a.price - b.price);
      setYtServices(yt);
    }).catch(() => {});
  }, []);

  const texts = content.site_text || {};
  const gallery = (content.home_gallery || []).map(imgUrl);
  const galleryImgs = gallery.length ? gallery : DEFAULT_GALLERY;
  const mvPhotos = (content.mv_photos || []).map(imgUrl);
  const mvImgs = mvPhotos.length ? mvPhotos : DEFAULT_MV;
  const testimonials = content.testimonials || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain min-h-[92vh] flex items-end overflow-hidden">
        <video data-testid="hero-video" autoPlay muted loop playsInline poster={HERO_POSTER}
          className="absolute inset-0 w-full h-full object-cover opacity-50">
          <source src={imgUrl("/api/files/bookedbybenj/site/hero.mp4")} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <p className="animate-fade-up text-xs tracking-[0.4em] uppercase text-brand mb-6" style={{ animationDelay: "0.1s" }}>
            Booked by Benj — Los Angeles
          </p>
          <h1 data-testid="hero-headline" className="animate-fade-up font-display text-6xl sm:text-7xl lg:text-9xl leading-[0.9] text-white max-w-5xl" style={{ animationDelay: "0.2s" }}>
            {texts.hero_title || <>Power Yourself<br />Like a <span className="text-brand">Label</span></>}
          </h1>
          <p className="animate-fade-up text-base md:text-lg text-zinc-300 mt-6 tracking-wide" style={{ animationDelay: "0.35s" }}>
            {texts.hero_tagline || <>Talent <span className="text-brand mx-1">·</span> Fleet <span className="text-brand mx-1">·</span> Production <span className="text-brand mx-1">·</span> Results</>}
          </p>
          <div className="animate-fade-up flex flex-wrap gap-4 mt-10" style={{ animationDelay: "0.5s" }}>
            <Link to="/marketing" data-testid="hero-campaigns-btn"
              className="bg-brand hover:bg-brand-hover text-white text-sm font-semibold tracking-widest uppercase px-8 py-4 transition-colors">
              Browse Campaigns
            </Link>
            <Link to="/consultation" data-testid="hero-consultation-btn"
              className="border border-white/30 hover:border-brand text-white text-sm font-semibold tracking-widest uppercase px-8 py-4 transition-colors">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">What we do</p>
        <h2 className="font-display text-5xl sm:text-6xl text-white mb-14">Four lanes. One call.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <Link key={s.to} to={s.to} data-testid={`service-card-${s.title.toLowerCase()}`}
              className="group relative overflow-hidden border border-white/10 hover:border-brand/60 transition-colors duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${i * 0.1}s` }}>
              <div className="aspect-[3/4] overflow-hidden">
                <img src={s.img} alt={s.title}
                  className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-0 p-6 w-full">
                <h3 className="font-display text-3xl text-white">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-brand mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUSTED */}
      <section data-testid="logo-marquee" className="border-y border-white/10 py-14 bg-[#0A0A0A]">
        <p className="text-center text-xs tracking-[0.4em] uppercase text-zinc-500 mb-10">Trusted by the Industry</p>
        <Marquee speed={40} gradient={false}>
          {[...LOGOS, ...LOGOS].map((l, i) => (
            <img key={i} src={l} alt="Industry logo"
              className="h-14 w-auto min-w-28 object-contain mx-10 opacity-60 hover:opacity-100 transition-opacity duration-300" />
          ))}
        </Marquee>
      </section>

      {/* YOUTUBE CAMPAIGNS STRIP */}
      {ytServices.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex items-center gap-4 mb-4">
            <img src="https://cdn.simpleicons.org/youtube/FF0000" alt="YouTube" className="h-8 w-8" />
            <h2 className="font-display text-4xl sm:text-5xl text-white">YouTube Campaigns</h2>
          </div>
          <p className="text-xs tracking-[0.25em] uppercase text-zinc-400 mb-10">
            All campaigns generate Views · Likes · Comments · Shares · Subscribers
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {ytServices.map((s) => (
              <Link key={s.id} to="/marketing" data-testid={`home-yt-${s.id}`}
                className="border border-white/10 hover:border-brand/60 bg-[#0A0A0A] p-8 transition-colors hover:-translate-y-1 group">
                <p className="text-[10px] tracking-[0.3em] uppercase text-brand">{s.name.split("—")[1]?.trim() || "Campaign"}</p>
                <p className="font-display text-5xl text-white mt-3">{fmt(s.price)}</p>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{s.description}</p>
                <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-brand mt-6 group-hover:gap-3 transition-all">
                  Start Campaign <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* MUSIC VIDEO */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-5xl sm:text-6xl text-white mb-4">Shooting a Music Video?</h2>
          <p className="text-sm md:text-base text-zinc-400 max-w-2xl mb-12">
            Models, exotic cars and mansions — everything your video needs, one booking away.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {mvImgs.slice(0, 3).map((img, i) => (
              <div key={i} data-testid={`mv-photo-${i}`} className="overflow-hidden border border-white/10 group">
                <img src={img} alt="Music video production" loading="lazy"
                  className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <span className="font-display text-3xl text-white">Contact Us</span>
            <a href="tel:3234947199" data-testid="contact-call-btn"
              className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-widest uppercase px-6 py-3.5 transition-colors">
              <Phone className="w-4 h-4" /> Call 323-494-7199
            </a>
            <a href="sms:3234947199" data-testid="contact-text-btn"
              className="flex items-center gap-2 border border-white/30 hover:border-brand text-white text-xs font-semibold tracking-widest uppercase px-6 py-3.5 transition-colors">
              <MessageSquare className="w-4 h-4" /> Text Us
            </a>
          </div>
        </div>
      </section>

      {/* CLIENT GALLERY */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">Recent work</p>
            <h2 className="font-display text-5xl sm:text-6xl text-white">Booked. Shot. Delivered.</h2>
          </div>
          <Link to="/consultation" className="text-xs tracking-widest uppercase text-zinc-400 hover:text-brand transition-colors flex items-center gap-2">
            Start yours <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {galleryImgs.map((g, i) => (
            <div key={i} data-testid={`gallery-item-${i}`}
              className={`overflow-hidden border border-white/10 group ${i % 3 === 0 ? "md:row-span-2" : ""}`}>
              <img src={g} alt="Client work" loading="lazy"
                className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${i % 3 === 0 ? "h-full min-h-[420px]" : "h-52 md:h-64"}`} />
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="border-t border-white/10 bg-[#0A0A0A] py-24">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">Testimonials</p>
            <h2 className="font-display text-5xl sm:text-6xl text-white mb-14">Word on the street.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <figure key={i} data-testid={`testimonial-${i}`} className="border border-white/10 p-8 bg-[#050505]">
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-brand text-brand" />)}
                  </div>
                  <blockquote className="text-sm text-zinc-300 leading-relaxed">"{t.text}"</blockquote>
                  <figcaption className="text-xs tracking-widest uppercase text-zinc-500 mt-6">— {t.name}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative grain border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-28 text-left">
          <h2 className="font-display text-5xl sm:text-7xl text-white max-w-3xl leading-[0.95]">
            Ready to move like a <span className="text-brand">major</span>?
          </h2>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link to="/marketing" data-testid="cta-marketing-btn"
              className="bg-brand hover:bg-brand-hover text-white text-sm font-semibold tracking-widest uppercase px-8 py-4 transition-colors">
              Browse Campaigns
            </Link>
            <Link to="/consultation" data-testid="cta-consultation-btn"
              className="border border-white/30 hover:border-brand text-white text-sm font-semibold tracking-widest uppercase px-8 py-4 transition-colors">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
